import { and, endsWith, isEmpty, not, or } from 'ramda';
import { isNil, map } from 'ramda';
import { z } from 'zod';
import { commonErrors } from './error';

export const checkbox = z
	.enum(['true'])
	.or(z.boolean())
	.nullish() // if you don't check a box it won't show up as having a value. Treat that as "false"
	.transform((x) => !!x);

export const zipValidation = /^(?!0{5})(\d{5})(?!-?0{4})(-\d{4})?$/;
export const zipCode = z.string().regex(zipValidation, commonErrors.invalidZip);

export const requiredCheckbox = (message: string) =>
	z
		.enum(['true'])
		.or(z.boolean())
		.transform((x) => !!x)
		.refine((data) => data, { message: message });

export const requiredString = (message: string, minLength = 1) =>
	z.string({ required_error: message }).min(minLength, { message });

export const requiredNumber = (message: string) => z.number({ required_error: message });

export const requiredStringArray = (message: string) =>
	z.array(z.string().min(1), { required_error: message });

export const requiredTagBoxArray = (message: string) =>
	z.array(
		z
			.object({
				label: z.string(),
			})
			.transform(({ label }) => label),
		{ required_error: message }
	);

export const optionalValue = z.any().nullish();
export const optionalString = z.string().nullish();
export const optionalNumber = z.number().nullish();
export const optionalStringArray = z.array(z.string().min(1)).nullish();
export const optionalTagBoxArray = z
	.array(
		z
			.object({
				label: z.string(),
			})
			.transform(({ label }) => label)
	)
	.nullish();

enum BoolRadioButtonOptions {
	Yes = 'yes',
	No = 'no',
}

export const optionalBoolRadioButton = z
	.string()
	.or(z.boolean())
	.nullish()
	.transform((x) => (isNil(x) ? x : x === 'yes' || x === true));

export const boolRadioButton = (message?: string) =>
	z
		.nativeEnum(BoolRadioButtonOptions, {
			errorMap: (issue, ctx) => ({
				message:
					issue.code == z.ZodIssueCode.invalid_enum_value && !!message ? message : ctx.defaultError,
			}),
		})
		.transform((x) => x === 'yes');

export const boolRadioButtonString = (x: boolean | undefined) =>
	isNil(x) ? x : x === true ? 'yes' : 'no';
export const tagBoxConverter = (values: string[]) =>
	map((v) => {
		return { value: v, label: v };
	}, values ?? []);

type ZodData = {
	[x: string]: string | boolean | string[] | null | undefined;
	[x: number]: string | boolean | string[] | null | undefined;
};
type SuperRefineHelperParams = {
	dataPath: string;
	negativeCondition: (v: string | boolean | undefined | null) => boolean;
	data: ZodData;
	context: z.RefinementCtx;
	message: string;
	additionalParams?: z.IssueData;
};
type SuperRefineHelper = (params: SuperRefineHelperParams) => void;
export const superRefineHelper: SuperRefineHelper = ({
	dataPath,
	negativeCondition,
	data,
	context,
	message,
	additionalParams = {
		code: z.ZodIssueCode.custom,
	},
}) => {
	const testData = data?.[dataPath];
	if (negativeCondition(testData)) {
		context.addIssue({
			...additionalParams,
			path: [dataPath],
			message: message,
		});
	}
};

export const commonParams = (data: ZodData, context: z.RefinementCtx) => {
	return { data: data, context: context };
};

export const isSelected = (val: string) =>
	and(not(or(endsWith('.no', val), endsWith('.dont_know', val))), not(isEmpty(val)));
